import { BLACKOUT_SCOPES, formatEntries, formatTimeRange, includesId, isAllDayEntry, isSameId } from "helpers/blackouts"

// Entry date/time formatting is shared with the Calendar surfaces — see `helpers/blackouts`.
export { isAllDayEntry, isRangeEntry, formatTimeRange, formatEntry, formatEntries } from "helpers/blackouts"

/**
 * The date line for a row: entries, plus the time range when a single entry is timed.
 * A multi-entry blackout keeps just the dates — per-entry times would not fit the row.
 */
export const formatRowDateLabel = (blackout) => {
  const entries = blackout?.entries || []
  const dates = formatEntries(entries)
  if (entries.length !== 1 || isAllDayEntry(entries[0])) return dates
  return `${dates} · ${formatTimeRange(entries[0])}`
}

/**
 * Row identity for an unsaved "Applies on" entry.
 *
 * Saved entries keep the server's `entries[].id`. This only fills in rows the guide has just
 * added, and rows the API returns without an id — `AppliesOnRows` keys, edits and deletes by
 * id, so two rows sharing one would be edited and removed together.
 */
export const createEntryId = () => `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

/**
 * A saved record's entries, each with a usable row id.
 *
 * The server's `entries[].id` is preferred — but only when a record's ids are actually distinct.
 * The API has shipped `"e-1"` on every entry, and now returns the record's own id on the entry,
 * so uniqueness is not something to assume. `AppliesOnRows` keys, edits and deletes rows by this
 * id, so a single collision would edit or remove several rows at once. Any duplicate or missing
 * id falls the whole record back to client ids, which are ignored on write (D6) and cost nothing.
 */
export const withRowIds = (entries = []) => {
  const ids = entries.map((entry) => entry.id)
  const areIdsUsable = ids.every(Boolean) && new Set(ids.map(String)).size === ids.length
  return entries.map((entry) => ({ ...entry, id: areIdsUsable ? entry.id : createEntryId() }))
}

/** A fresh, empty "Applies on" row. */
export const createEmptyEntry = () => ({
  id: createEntryId(),
  start_date: null,
  end_date: null,
  start_time: null,
  end_time: null
})

export const ENTRY_ERRORS = {
  MISSING_DATE: "missing_date",
  INCOMPLETE_TIME: "incomplete_time",
  TIME_ORDER: "time_order"
}

/**
 * What is wrong with one "Applies on" row, or null.
 *
 * A dateless row is only an error once the guide has added a second one: the modal opens with a
 * single empty row, and flagging that would put an error on an untouched form. With more than one
 * row it matters — `buildBlackoutPayload` drops undated rows, so without this the guide fills two
 * of three rows, saves, and the third is gone with nothing said.
 *
 * Times are compared as "HH:mm" strings, which orders correctly. An end at or before the start is
 * rejected rather than read as crossing midnight — an entry's times apply *within* each of its
 * days, so there is no shape here that can express an overnight window.
 */
export const getEntryError = (entry, { isOnlyEntry = false } = {}) => {
  if (!entry?.start_date) return isOnlyEntry ? null : ENTRY_ERRORS.MISSING_DATE

  const hasStartTime = Boolean(entry.start_time)
  const hasEndTime = Boolean(entry.end_time)
  if (hasStartTime !== hasEndTime) return ENTRY_ERRORS.INCOMPLETE_TIME
  if (hasStartTime && entry.end_time <= entry.start_time) return ENTRY_ERRORS.TIME_ORDER

  return null
}

/** Row errors keyed by entry id; an empty object means the whole list is valid. */
export const getEntryErrors = (entries = []) =>
  entries.reduce((errors, entry) => {
    const error = getEntryError(entry, { isOnlyEntry: entries.length === 1 })
    if (error) errors[entry.id] = error
    return errors
  }, {})

/**
 * Flattens the nested target options into the leaf ids the API stores.
 * Selecting a parent (e.g. "Kayak · 5 assets") means selecting all of its assets.
 */
export const collectSelectableIds = (options = []) =>
  options.reduce((ids, option) => {
    if (option.children?.length) return [...ids, ...option.children.map((child) => child.id)]
    return [...ids, option.id]
  }, [])

/** Looks up an option or child option by id so chips can render its label. */
export const findOptionById = (options = [], id) => {
  for (const option of options) {
    if (isSameId(option.id, id)) return option
    const child = option.children?.find((candidate) => isSameId(candidate.id, id))
    if (child) return child
  }
  return null
}

/** "Leslie Alexander (Capitan)" — label plus optional suffix. */
export const formatOptionLabel = (option) => (option?.suffix ? `${option.label} ${option.suffix}` : option?.label || "")

/** True when every child of a parent option is selected. */
export const areAllChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) && option.children.every((child) => includesId(selectedIds, child.id))

/** True when some but not all children are selected — the parent renders indeterminate. */
export const areSomeChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) &&
  option.children.some((child) => includesId(selectedIds, child.id)) &&
  !areAllChildrenSelected(option, selectedIds)

/**
 * Chips collapse to the parent when all of its children are selected, and expand to
 * individual assets otherwise — the two states visible in the equipment design.
 */
export const buildSelectedChips = (options = [], selectedIds = []) =>
  options.reduce((chips, option) => {
    if (!option.children?.length) {
      return includesId(selectedIds, option.id) ? [...chips, { id: option.id, label: formatOptionLabel(option) }] : chips
    }
    if (areAllChildrenSelected(option, selectedIds)) {
      return [...chips, { id: option.id, label: formatOptionLabel(option), childIds: option.children.map((child) => child.id) }]
    }
    const selectedChildren = option.children.filter((child) => includesId(selectedIds, child.id))
    return [...chips, ...selectedChildren.map((child) => ({ id: child.id, label: formatOptionLabel(child) }))]
  }, [])

/**
 * Strips UI-only fields before sending a blackout to the API.
 *
 * A business blackout applies to everything and has no picker, so `target_ids` is forced empty
 * rather than passed through — the API has been seen returning a non-empty `target_ids` on a
 * business record, and editing must not echo that back as if the guide had chosen it.
 */
export const buildBlackoutPayload = ({ scope, reason, isRecurringYearly, entries, selectedIds }) => ({
  scope,
  reason: reason?.trim() || "",
  recurring_yearly: Boolean(isRecurringYearly),
  entries: entries
    .filter((entry) => entry.start_date)
    .map((entry) => ({
      start_date: entry.start_date,
      end_date: entry.end_date || entry.start_date,
      start_time: entry.start_time || null,
      end_time: entry.end_time || null
    })),
  target_ids: scope === BLACKOUT_SCOPES.BUSINESS ? [] : selectedIds || []
})
